import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import {
  Activity,
  Brain,
  Cpu,
  Database,
  GitBranch,
  Search,
  ChevronRight,
  Zap,
  AlertTriangle,
  Layers,
  Settings,
  Play,
  ArrowLeft,
  Plus,
} from "lucide-react";

import MemoryGraph from "@/components/MemoryGraph";
import {
  initialNodes,
  initialLinks,
  eventStreamSeed,
  confidenceSeries,
  agentList,
  proceduresList,
} from "@/lib/mockData";

// ────────────────────────────────────────────────────────────
// Sidebar
function Sidebar({ active, setActive }) {
  const nav = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "agents", label: "Agents", icon: Cpu },
    { id: "memory", label: "Memory graph", icon: Brain },
    { id: "lessons", label: "Lessons", icon: Layers },
    { id: "procedures", label: "Procedures", icon: GitBranch },
    { id: "stream", label: "Event stream", icon: Database },
    { id: "settings", label: "Settings", icon: Settings },
  ];
  return (
    <aside className="w-60 shrink-0 border-r border-line-1 bg-surface min-h-[calc(100vh-3.5rem)] sticky top-14">
      <div className="p-4 border-b border-line-1">
        <Link
          to="/"
          data-testid="dashboard-back-link"
          className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ink-500 hover:text-cyber"
        >
          <ArrowLeft size={12} /> back to site
        </Link>
      </div>
      <div className="p-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 px-3 py-2">
          Tenant · acme-labs
        </div>
        <nav className="space-y-0.5">
          {nav.map((n) => (
            <button
              key={n.id}
              data-testid={`sidebar-${n.id}`}
              onClick={() => setActive(n.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-[12px] tracking-[0.15em] uppercase border-l-2 ${
                active === n.id
                  ? "border-cyber text-cyber bg-cyber/5"
                  : "border-transparent text-ink-300 hover:text-ink-100 hover:border-line-2"
              }`}
            >
              <n.icon size={14} strokeWidth={1.5} /> {n.label}
            </button>
          ))}
        </nav>

        <div className="mt-8 px-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-2">
            Active agents
          </div>
          <ul className="space-y-1.5">
            {agentList.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between text-[11px] text-ink-300"
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-1.5 h-1.5 ${
                      a.health > 0.85
                        ? "bg-terminal"
                        : a.health > 0.75
                          ? "bg-amber"
                          : "bg-hotpink"
                    }`}
                  />
                  {a.id}
                </span>
                <span className="text-ink-500">{Math.round(a.health * 100)}%</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}

// ────────────────────────────────────────────────────────────
// Metric tile
function Metric({ label, value, delta, deltaTone = "cyan", icon: Icon, sparkline }) {
  const toneCls =
    deltaTone === "cyan" ? "text-cyber" : deltaTone === "amber" ? "text-amber" : "text-hotpink";
  return (
    <div className="border border-line-1 bg-surface p-5 relative group hover:border-line-2 transition-colors">
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">{label}</div>
        <Icon size={14} className="text-ink-500 group-hover:text-cyber" strokeWidth={1.5} />
      </div>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-display font-black text-3xl text-ink-100">{value}</div>
        <div className={`text-[11px] tracking-[0.2em] uppercase ${toneCls}`}>{delta}</div>
      </div>
      {sparkline && (
        <div className="h-10 mt-3 -mx-1" style={{ minWidth: 0 }}>
          <ResponsiveContainer width="99%" height="100%">
            <AreaChart data={sparkline} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`sp-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="#00E5FF"
                strokeWidth={1.4}
                fill={`url(#sp-${label})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Confidence chart
function ConfidenceChart() {
  return (
    <div className="border border-line-1 bg-surface p-5 col-span-1 md:col-span-2">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            Confidence dynamics · last 30 runs
          </div>
          <div className="font-display font-bold text-lg mt-1">
            mnemo-trained <span className="text-cyber">vs</span> stateless baseline
          </div>
        </div>
        <div className="flex gap-3 text-[10px] tracking-[0.3em] uppercase">
          <span className="flex items-center gap-1.5 text-cyber">
            <span className="w-3 h-px bg-cyber" /> mnemo
          </span>
          <span className="flex items-center gap-1.5 text-ink-500">
            <span className="w-3 h-px bg-ink-500" /> baseline
          </span>
        </div>
      </div>
      <div className="h-64 -ml-2">
        <ResponsiveContainer>
          <LineChart data={confidenceSeries}>
            <CartesianGrid stroke="#1A1A24" strokeDasharray="2 4" vertical={false} />
            <XAxis
              dataKey="run"
              stroke="#8A8A98"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "#1A1A24" }}
            />
            <YAxis
              stroke="#8A8A98"
              fontSize={10}
              domain={[0, 1]}
              tickLine={false}
              axisLine={{ stroke: "#1A1A24" }}
            />
            <Tooltip
              contentStyle={{
                background: "#0C0C10",
                border: "1px solid #1A1A24",
                borderRadius: 0,
                fontFamily: "IBM Plex Mono",
                fontSize: 11,
              }}
              labelStyle={{ color: "#8A8A98", textTransform: "uppercase", letterSpacing: "0.2em" }}
            />
            <Line
              type="monotone"
              dataKey="baseline"
              stroke="#8A8A98"
              strokeWidth={1.2}
              dot={false}
              strokeDasharray="3 3"
            />
            <Line
              type="monotone"
              dataKey="mnemo"
              stroke="#00E5FF"
              strokeWidth={2}
              dot={{ fill: "#FFAB00", stroke: "#FFAB00", r: 2 }}
              activeDot={{ r: 4, fill: "#00E5FF" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Event stream
function EventStream({ events }) {
  const tone = (t) =>
    t === "cyan" ? "text-cyber" : t === "amber" ? "text-amber" : t === "pink" ? "text-hotpink" : "text-terminal";

  return (
    <div className="border border-line-1 bg-surface p-5 flex flex-col h-full min-h-[420px]">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            Live · event stream
          </div>
          <div className="font-display font-bold text-lg mt-1">tail -f reflection.log</div>
        </div>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-cyber">
          <span className="w-1.5 h-1.5 bg-cyber animate-pulse" /> streaming
        </div>
      </div>
      <div className="flex-1 overflow-hidden border border-line-1 bg-void/60 p-3 font-mono text-[12px] leading-6 scanlines" data-testid="event-stream">
        <div className="space-y-1">
          {events.map((e, i) => (
            <div key={i} className="flex gap-3">
              <span className="text-ink-500 shrink-0">[{e.t}]</span>
              <span className={`${tone(e.tone)} shrink-0 w-20`}>{e.kind}</span>
              <span className="text-ink-300 truncate">{e.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Procedures
function ProceduresWidget() {
  return (
    <div className="border border-line-1 bg-surface">
      <div className="flex items-center justify-between p-5 border-b border-line-1">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
            top procedures
          </div>
          <div className="font-display font-bold text-lg mt-1">promoted workflows</div>
        </div>
        <button
          data-testid="procedures-new"
          className="text-[10px] tracking-[0.3em] uppercase text-cyber border border-cyber/40 px-3 py-1.5 hover:bg-cyber/10"
        >
          <Plus size={12} className="inline mr-1" /> new
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] tracking-[0.3em] uppercase text-ink-500 border-b border-line-1">
            <th className="text-left px-5 py-2 font-normal">id</th>
            <th className="text-left px-5 py-2 font-normal">name</th>
            <th className="text-left px-5 py-2 font-normal">tags</th>
            <th className="text-right px-5 py-2 font-normal">uses</th>
            <th className="text-right px-5 py-2 font-normal">conf</th>
            <th className="px-5 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {proceduresList.map((p) => (
            <tr
              key={p.id}
              data-testid={`procedure-row-${p.id}`}
              className="border-b border-line-1 hover:bg-elevated transition-colors group"
            >
              <td className="px-5 py-3 text-ink-500 font-mono text-xs">{p.id}</td>
              <td className="px-5 py-3 text-ink-100">{p.name}</td>
              <td className="px-5 py-3">
                <div className="flex gap-1">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] tracking-[0.2em] uppercase border border-line-2 text-ink-300 px-1.5 py-0.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-5 py-3 text-right text-ink-300 font-mono">{p.uses}</td>
              <td className="px-5 py-3 text-right">
                <span
                  className={`font-mono text-xs ${
                    p.conf > 0.9 ? "text-cyber" : p.conf > 0.8 ? "text-amber" : "text-hotpink"
                  }`}
                >
                  {p.conf.toFixed(2)}
                </span>
              </td>
              <td className="px-5 py-3 text-right">
                <ChevronRight
                  size={14}
                  className="text-ink-500 group-hover:text-cyber inline"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Page
export default function Dashboard() {
  const [active, setActive] = useState("overview");
  const [events, setEvents] = useState(eventStreamSeed);
  const [nodes] = useState(() => initialNodes.map((n) => ({ ...n })));
  const [links] = useState(() => initialLinks.map((l) => ({ ...l })));
  const [highlight, setHighlight] = useState(null);

  // Live ticker - prepend a fresh event every few seconds
  useEffect(() => {
    const samples = [
      { kind: "CAPTURE", text: "agent=ops-2 · success=true · t=1.1s", tone: "green" },
      { kind: "REFLECT", text: "lesson generated · 'jitter exp backoff' · conf=0.71", tone: "cyan" },
      { kind: "RECALL", text: "matched 2 lessons for task='retry transient 502'", tone: "cyan" },
      { kind: "WARN", text: "lesson 0xA12 confidence decayed → 0.34", tone: "pink" },
      { kind: "CAPTURE", text: "agent=coder-7 · success=false · retry queued", tone: "amber" },
      { kind: "REFLECT", text: "procedure promoted · 'Vector index tuning' · conf=0.78", tone: "cyan" },
    ];
    const t = setInterval(() => {
      const e = samples[Math.floor(Math.random() * samples.length)];
      const now = new Date();
      const stamp = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}:${now.getSeconds().toString().padStart(2, "0")}`;
      setEvents((p) => [{ t: stamp, ...e }, ...p].slice(0, 18));
    }, 2400);
    return () => clearInterval(t);
  }, []);

  // Pulse a random node every now and then
  useEffect(() => {
    const t = setInterval(() => {
      const r = nodes[Math.floor(Math.random() * nodes.length)];
      setHighlight(r?.id);
    }, 2800);
    return () => clearInterval(t);
  }, [nodes]);

  const sparkline = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        v: 0.5 + Math.sin(i / 2) * 0.2 + Math.random() * 0.1,
      })),
    [],
  );

  return (
    <div className="App noise min-h-screen bg-void text-ink-100">
      {/* Compact top nav for dashboard */}
      <div className="border-b border-line-1 bg-void/80 backdrop-blur sticky top-0 z-50">
        <div className="px-6 h-14 flex items-center justify-between">
          <Link to="/" data-testid="dashboard-logo" className="flex items-center gap-2">
            <div className="w-7 h-7 border border-cyber/60 flex items-center justify-center">
              <Cpu size={14} className="text-cyber" strokeWidth={1.5} />
            </div>
            <span className="font-display font-black tracking-tight">
              MNEMO<span className="text-cyber">/</span>
              <span className="text-ink-500 text-xs">control.room</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-2 border border-line-1 bg-surface px-3 py-1.5 text-xs text-ink-300 w-80">
            <Search size={12} className="text-ink-500" />
            <input
              data-testid="dashboard-search"
              placeholder="search lessons, procedures, agents…"
              className="bg-transparent flex-1 focus:outline-none placeholder:text-ink-500"
            />
            <span className="text-ink-500 text-[10px] border border-line-2 px-1">⌘K</span>
          </div>
          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase">
            <span className="flex items-center gap-1.5 text-terminal">
              <span className="w-1.5 h-1.5 bg-terminal animate-pulse" /> reflection.engine · ok
            </span>
            <button
              data-testid="dashboard-run-reflection"
              className="bg-cyber text-void px-3 py-1.5 font-bold hover:shadow-[0_0_12px_rgba(0,229,255,0.5)]"
            >
              <Play size={11} className="inline mr-1" /> run.reflect
            </button>
          </div>
        </div>
      </div>

      <div className="flex">
        <Sidebar active={active} setActive={setActive} />

        <main className="flex-1 min-w-0 p-6 md:p-8 space-y-6">
          {/* Title */}
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
                / control.room / overview
              </div>
              <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">
                Welcome back, <span className="text-cyber">operator</span>.
              </h1>
              <p className="text-ink-500 text-sm mt-2">
                4 agents online · 441 lessons · 12 procedures · last reflection 11s ago
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-ink-500">
              <span>tenant_id:</span>
              <code className="text-cyber">acm_4f9e21</code>
            </div>
          </div>

          {/* Metrics row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              label="Memories captured"
              value="12,847"
              delta="+412 / 24h"
              icon={Database}
              sparkline={sparkline}
            />
            <Metric
              label="Lessons learned"
              value="441"
              delta="+18 / 24h"
              icon={Layers}
              sparkline={sparkline.map((s, i) => ({ v: s.v + i * 0.01 }))}
            />
            <Metric
              label="Avg confidence"
              value="0.84"
              delta="↑ 0.06"
              deltaTone="amber"
              icon={Brain}
              sparkline={sparkline}
            />
            <Metric
              label="Contradictions"
              value="3"
              delta="needs review"
              deltaTone="pink"
              icon={AlertTriangle}
              sparkline={sparkline.map((s) => ({ v: 1 - s.v }))}
            />
          </div>

          {/* Chart + Stream row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ConfidenceChart />
            <EventStream events={events} />
          </div>

          {/* Memory graph + Procedures */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 border border-line-1 bg-surface relative vignette">
              <div className="flex items-center justify-between p-5 border-b border-line-1">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
                    agent · coder-7
                  </div>
                  <div className="font-display font-bold text-lg mt-1">
                    neural.memory.graph <span className="text-cyber">●</span>
                  </div>
                </div>
                <div className="flex gap-2 text-[10px] tracking-[0.3em] uppercase">
                  <button className="border border-line-2 text-ink-300 px-2 py-1 hover:border-cyber hover:text-cyber" data-testid="graph-filter-all">
                    all
                  </button>
                  <button className="border border-line-2 text-ink-300 px-2 py-1 hover:border-cyber hover:text-cyber" data-testid="graph-filter-lessons">
                    lessons
                  </button>
                  <button className="border border-line-2 text-ink-300 px-2 py-1 hover:border-cyber hover:text-cyber" data-testid="graph-filter-procs">
                    procs
                  </button>
                </div>
              </div>
              <MemoryGraph nodes={nodes} links={links} height={460} highlightId={highlight} compact />
            </div>

            <div className="border border-line-1 bg-surface p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500">
                reflection · queue
              </div>
              <div className="font-display font-bold text-lg mt-1 mb-4">next 3 jobs</div>
              <ul className="space-y-3">
                {[
                  { id: "evt_0x4F2", task: "stream JSON parser", eta: "~3s" },
                  { id: "evt_0x501", task: "alembic drift fix", eta: "~7s" },
                  { id: "evt_0x510", task: "redis token bucket", eta: "~12s" },
                ].map((j, i) => (
                  <li
                    key={j.id}
                    className="flex items-center justify-between border border-line-1 p-3 hover:border-cyber/40 transition-colors"
                  >
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-ink-500">
                        {j.id}
                      </div>
                      <div className="text-sm text-ink-100 mt-0.5">{j.task}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-cyber">
                        eta {j.eta}
                      </div>
                      <div className="mt-1 h-1 w-20 bg-line-1 relative overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-cyber"
                          style={{ width: `${30 + i * 25}%` }}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-line-1">
                <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 mb-2">
                  reflection.engine
                </div>
                <div className="space-y-1.5 text-xs font-mono text-ink-300">
                  <div className="flex justify-between"><span>model</span><span className="text-cyber">claude-sonnet-4.5</span></div>
                  <div className="flex justify-between"><span>queue</span><span>redis://6379</span></div>
                  <div className="flex justify-between"><span>worker</span><span className="text-terminal">arq · pid 4187</span></div>
                  <div className="flex justify-between"><span>p95 latency</span><span className="text-amber">412ms</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Procedures table */}
          <ProceduresWidget />

          {/* Footer hint */}
          <div className="text-[10px] tracking-[0.3em] uppercase text-ink-500 text-center py-6">
            this is a mocked control room · all data simulated · no agents were harmed
          </div>
        </main>
      </div>
    </div>
  );
}
